import { CheckCircle, GitBranch, BarChart3, Zap } from 'lucide-react';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: <CheckCircle className="w-6 h-6" />,
    title: 'Human-in-the-Middle by Default',
    description:
      'Every AI employee starts with full human review. As they prove reliable, they earn autonomy through demonstrated performance — never the other way around.',
  },
  {
    icon: <GitBranch className="w-6 h-6" />,
    title: 'Recursive Self-Improvement',
    description:
      'One correction improves every bot that shares that capability. Your workforce gets smarter every week without retraining or redeployment.',
  },
  {
    icon: <BarChart3 className="w-6 h-6" />,
    title: 'Confidence-Based Autonomy',
    description:
      'Every bot has a transparent confidence score: (approvals / total decisions) × 100. You always know exactly why a bot was promoted — or demoted.',
  },
  {
    icon: <Zap className="w-6 h-6" />,
    title: 'Scope-Aware Governance',
    description:
      'Different approval rules for internal vs. external actions. A fundraising bot can draft emails freely but needs sign-off before sending to investors.',
  },
];

export const FeaturesSection = () => {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
          We Solve the Five Failure Modes
        </h2>
        <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
          Models hallucinate 3–12%. Agents fail most tasks. AI forgets a third of what you teach it, costs swing 100×, and the market is burned on trust. Those are not reasons to avoid AI — they are reasons to govern it. This is the governance.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
        {features.map((feature, idx) => (
          <div
            key={idx}
            className="p-6 md:p-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-lg transition-shadow"
            data-aos="fade-up"
            data-aos-delay={idx * 100}
          >
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-100 to-teal-100 dark:from-cyan-900 dark:to-teal-900 flex items-center justify-center text-cyan-600 dark:text-cyan-400 mb-4">
              {feature.icon}
            </div>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              {feature.title}
            </h3>
            <p className="text-slate-600 dark:text-slate-300">
              {feature.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
